import React, {
    useCallback, useContext, useMemo, useState
} from 'react'
import { useDispatch } from 'react-redux'
import { _, colors, withCapture } from 'utils'
import styled from 'styled-components'
import DataGrid, { ColumnDataGrid, DataGridProps } from 'components/DataGrid'
import Icon from 'components/Icon'
import FullPageLoading from 'components/FullPageLoading'
import { UserType } from 'generated/graphql'
import { CardHeader } from '../../../components'
import Button from '../../../components/Button'
import { addModal } from '../../modals/modalSlice'
import InviteUser from '../InviteUser'
import { removeOrganizationAdmin } from '../graphql/mutation'
import { OrganizationContext } from '../Organization'
import DropdownButton from '../../../components/DropdownButton'
import { showNotification } from '../../notifications'
import { showErrorNotification } from '../../notifications/notificationsSlice'

const S = {
    TopToolbar: styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        width: 100%;
    `,
    Body: styled.div`
        width: 100%;
        padding: 0 20px;
    `,
    DropdownButton: styled(DropdownButton)`
        font-size: 14px;
    `,
    DataGrid: styled(DataGrid)`
        width: calc(100% + 40px);
        margin: 0 -20px;
        & .datagrid__row-header, & .datagrid__row-item {
            border-bottom: 1px solid ${colors.gray1};
        }
        & .datagrid__row-item {
            height: 52px;
            font-size: 16px;
            color: ${colors.grayTitle};
        }
        & .datagrid__column-header:first-child {
            padding-left: 20px;
        }
        & .infinite-scroll-component {
            overflow: visible !important;
        }
    `,
    Action: styled.div`
        text-align: right;
        white-space: nowrap;
        padding-right: 8px;
    `,
}

const createColumns = ({ onDelete }): ColumnDataGrid[] => [
    {
        name: 'name',
        width: 4,
        header: _('organization.users.list.name'),
        render: (user: UserType) => {
            const isPending = !user.hasLoggedOn
            return (
                <span style={{ marginLeft: 10 }}>
                    {isPending ? _('organization.users.unknown') : `${user.firstName} ${user.lastName}`}
                </span>
            )
        }
    },
    {
        name: 'email',
        width: 5,
        header: _('organization.users.list.email'),
    },
    {
        name: 'pending',
        width: 2,
        render: (user: UserType) => {
            const isPending = !user.hasLoggedOn
            return (
                <span style={{ marginLeft: 10 }}>
                    {isPending && _('organization.users.pending')}
                </span>
            )
        }
    },
    {
        name: 'actions',
        width: 1,
        render: (user: UserType) => (
            <S.Action>
                <Icon icon="trash" className="ml-2" size={20} onClick={() => onDelete(user)} />
            </S.Action>
        )
    }
]

type UserDataGridProps = Omit<DataGridProps, 'data'|'columns'> & {
    users: Partial<UserType>[]
    onDelete: (id: number) => void
}
const UserDataGrid = ({
    users,
    onDelete,
    ...props
}: UserDataGridProps) => {
    const columns = useMemo(() => createColumns({ onDelete }), [onDelete])

    return (
        <S.DataGrid
            data={users}
            columns={columns}
            hideHeader={false}
            hideAction
            theme="light"
            {...props}
        />
    )
}

const Users = () => {
    const [loading, setLoading] = useState(false)
    const { organization, refresh } = useContext(OrganizationContext)
    const dispatch = useDispatch()

    const showAddModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <InviteUser close={close} afterSubmit={refresh} organizationId={`${organization?.id}`} />
            ),
            shouldNotWrap: true
        }))
    }, [dispatch, organization?.id, refresh])

    const handleDelete = withCapture(async (user) => {
        setLoading(true)
        const response = await removeOrganizationAdmin({
            organizationId: `${organization.id}`,
            email: user.email
        })
        await refresh()
        setLoading(false)

        if (response.data) {
            dispatch(showNotification({
                type: 'success',
                body: _('form.notification.update_success'),
            }))
            return true
        }

        dispatch(showErrorNotification(response.errors))
        return false
    })

    return (
        <>
            <S.TopToolbar>
                <CardHeader>
                    {_('organization.users.list_header')}
                </CardHeader>
                <Button
                    icon="plusBox"
                    size="sm"
                    onClick={showAddModal}
                    disabled={loading}
                >
                    {_('organization.users.add')}
                </Button>
            </S.TopToolbar>
            {loading && <FullPageLoading isAbsolute />}
            <S.Body>
                {!loading && (<UserDataGrid users={organization.admins} onDelete={handleDelete} />)}
            </S.Body>
        </>
    )
}

export default Users
